/**
 * Request/response schemas for the OB/GYN specialty module (`BACKLOG.md` M11). Same reasoning as
 * `pediatricsRequests.ts`: these live in `shared/` so the server validates against the exact schema
 * the web app infers its types from, every object schema is strict, and no server-owned value (an
 * id, `orgId`, gestational age, the computed EDD/redate decision) appears in any request schema —
 * the server always derives those from `datingEngine.ts`/the session, never accepts them from the
 * client.
 */

import { z } from 'zod';

import { calendarDateSchema, confidentialityReasonsInputSchema } from './clinicalRequests.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import { OBGYN_SCREENING_IDS } from './obgyn/screeningOrderSets.ts';
import { EPDS_ITEM_COUNT, EPDS_MAX_ITEM_SCORE } from './obgyn/epds.ts';

/**
 * Upper bounds matching `db/schema/obgyn.ts`'s own `numeric` column precision exactly — without
 * these, a value the DB's own column can't hold (e.g. `numeric(5,1)`'s real max of 9999.9) reaches
 * Postgres and fails there instead of being refused here as a clean `400`, the same "validate at the
 * boundary, not let the database's own constraint be the first thing that notices" reasoning behind
 * every other bound in this file.
 */
const MAX_NUMERIC_5_1 = 9999.9; // precision 5, scale 1 — crlMeasurementMm, fundalHeightCm, bpd/hc/ac/flMm
const MAX_NUMERIC_6_2 = 9999.99; // precision 6, scale 2 — weightKg

// --- Pregnancy episodes -----------------------------------------------------------------------------

/**
 * `lmpDate` and the `crlScanDate`/`crlMeasurementMm` pair are each optional individually, but two
 * real rules govern them together — enforced with `superRefine` rather than a single `refine` so
 * each gets its own distinct issue path/message instead of one combined boolean that can't tell a
 * caller which specific rule it broke:
 *
 *   1. At least one complete dating input must be present — `datingEngine.ts`'s `determineDating`
 *      needs one to compute an EDD at all.
 *   2. `crlScanDate` and `crlMeasurementMm` are a pair: supplying one without the other would create
 *      an episode carrying a stored scan date with no measurement (or vice versa) — an orphaned half
 *      of a CRL reading that `determineDating` can never actually use.
 */
export const createPregnancyEpisodeRequestSchema = z
  .object({
    encounterId: z.uuid().optional(),
    lmpDate: calendarDateSchema.optional(),
    crlScanDate: calendarDateSchema.optional(),
    crlMeasurementMm: z.number().positive().max(MAX_NUMERIC_5_1).optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .superRefine((v, ctx) => {
    const crlScanDateGiven = v.crlScanDate !== undefined;
    const crlMeasurementMmGiven = v.crlMeasurementMm !== undefined;
    if (crlScanDateGiven !== crlMeasurementMmGiven) {
      const missing = crlScanDateGiven ? 'crlMeasurementMm' : 'crlScanDate';
      ctx.addIssue({
        code: 'custom',
        path: [missing],
        message: `${missing} is required once its pair is supplied — a CRL reading needs both fields together`,
      });
      return;
    }
    if (v.lmpDate === undefined && !crlScanDateGiven) {
      ctx.addIssue({
        code: 'custom',
        message: 'either lmpDate or both crlScanDate and crlMeasurementMm must be supplied',
      });
    }
  });
export type CreatePregnancyEpisodeRequest = z.infer<typeof createPregnancyEpisodeRequestSchema>;

export const DELIVERY_MODES = ['vaginal', 'cesarean'] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

/** A real type guard, not an unchecked cast, matching `pediatrics/routes.ts`'s
 * `isGrowthMeasurementType` — narrows a DB-read `string` (`deliveryMode` is stored as plain `text`
 * with a `CHECK` constraint, not a Postgres enum) to the closed type `selectAntepartumBilling`
 * requires. A row that somehow fails the check (not reachable in ordinary operation, since the
 * write path always validates through `recordDeliveryRequestSchema` first) is treated as "no
 * delivery mode known" rather than mistyped. */
export function isDeliveryMode(value: string): value is DeliveryMode {
  return (DELIVERY_MODES as readonly string[]).includes(value);
}

/** Manual delivery-summary entry (`BACKLOG.md` M11's explicit in-scope shape — HL7/CCD ingestion is
 * the deferred, automated version of this same data). Allowed once per episode; the service refuses
 * a second call rather than overwriting a recorded delivery. */
export const recordDeliveryRequestSchema = z
  .object({
    deliveryDate: calendarDateSchema,
    deliveryMode: z.enum(DELIVERY_MODES),
    deliveredByThisPractice: z.boolean(),
    deliveryNotes: z.string().max(4000).optional(),
  })
  .strict();
export type RecordDeliveryRequest = z.infer<typeof recordDeliveryRequestSchema>;

export const PREGNANCY_EPISODE_STATUSES = ['active', 'delivered', 'ended'] as const;

export const pregnancyEpisodeResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  // Response, not request — plain string, matching `clinicalRequests.ts`'s own split
  // (`vitalResponseSchema.vitalType`, e.g.): the narrow enum guards what a caller may *write*
  // (`createPregnancyEpisodeRequestSchema` never accepts `datingMethod` directly at all — it's
  // always server-derived by `datingEngine.ts`), and a value already stored/computed is read back
  // as-is, with no cast needed at the service→response boundary.
  datingMethod: z.string(),
  lmpDate: calendarDateSchema.nullable(),
  crlMeasurementMm: z.number().nullable(),
  crlMeasurementDate: calendarDateSchema.nullable(),
  estimatedDueDate: calendarDateSchema,
  // Plain string, not `z.iso.datetime()` — a DB readback of an `instant()` column (Postgres's own
  // timestamptz wire format, not strict ISO 8601), matching `dermatologyRequests.ts`'s
  // `fitzpatrickRecordedAt` split between a strict request validator and a lenient response readback.
  eddLockedAt: z.string().nullable(),
  redatedFromEdd: calendarDateSchema.nullable(),
  // Plain string, not `z.enum(PREGNANCY_EPISODE_STATUSES)` — same response/request split as
  // `datingMethod` above: `status` is server-derived (create/`recordDelivery`), never client-set.
  status: z.string(),
  deliveryDate: calendarDateSchema.nullable(),
  // Plain string, not `z.enum(DELIVERY_MODES)` — a read-back of a value the request schema (line 49
  // above) already validated as strict at write time; the response side never re-narrows it.
  deliveryMode: z.string().nullable(),
  deliveredByThisPractice: z.boolean().nullable(),
  deliveryNotes: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  gestationalAgeToday: z.object({ weeks: z.number().int(), days: z.number().int() }).nullable(),
});
export type PregnancyEpisodeResponse = z.infer<typeof pregnancyEpisodeResponseSchema>;

export const pregnancyEpisodeEnvelopeSchema = z.object({ episode: pregnancyEpisodeResponseSchema });
export const pregnancyEpisodeListEnvelopeSchema = z.object({
  episodes: z.array(pregnancyEpisodeResponseSchema),
});

/** The create-episode response specifically: the episode itself, plus `crlRejected` — transient
 * feedback about *this* request (a supplied CRL measurement was out of range and unusable for
 * dating), not a stored fact returned by any other route. See `datingEngine.ts`'s
 * `DatingDecision.crlRejected`. */
export const createPregnancyEpisodeEnvelopeSchema = z.object({
  episode: pregnancyEpisodeResponseSchema,
  crlRejected: z.boolean(),
});

// --- Antepartum flowsheet visits ---------------------------------------------------------------------

const URINE_DIP_VALUES = ['negative', 'trace', '1+', '2+', '3+', '4+'] as const;

export const createAntepartumVisitRequestSchema = z
  .object({
    encounterId: z.uuid().optional(),
    visitDate: calendarDateSchema,
    weightKg: z.number().positive().max(MAX_NUMERIC_6_2).optional(),
    systolicBp: z.number().int().positive().optional(),
    diastolicBp: z.number().int().positive().optional(),
    fundalHeightCm: z.number().positive().max(MAX_NUMERIC_5_1).optional(),
    fetalHeartToneBpm: z.number().int().positive().optional(),
    urineDipProtein: z.enum(URINE_DIP_VALUES).optional(),
    urineDipGlucose: z.enum(URINE_DIP_VALUES).optional(),
    notes: z.string().max(4000).optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateAntepartumVisitRequest = z.infer<typeof createAntepartumVisitRequestSchema>;

export const antepartumVisitResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  pregnancyEpisodeId: z.uuid(),
  encounterId: z.uuid().nullable(),
  visitDate: calendarDateSchema,
  gestationalAge: z.object({ weeks: z.number().int(), days: z.number().int() }),
  weightKg: z.number().nullable(),
  systolicBp: z.number().int().nullable(),
  diastolicBp: z.number().int().nullable(),
  fundalHeightCm: z.number().nullable(),
  fetalHeartToneBpm: z.number().int().nullable(),
  urineDipProtein: z.string().nullable(),
  urineDipGlucose: z.string().nullable(),
  notes: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
});
export type AntepartumVisitResponse = z.infer<typeof antepartumVisitResponseSchema>;

export const antepartumVisitEnvelopeSchema = z.object({ visit: antepartumVisitResponseSchema });
export const antepartumVisitListEnvelopeSchema = z.object({
  visits: z.array(antepartumVisitResponseSchema),
});

// --- Ultrasound biometry -----------------------------------------------------------------------------

export const createUltrasoundBiometryRequestSchema = z
  .object({
    encounterId: z.uuid().optional(),
    scanDate: calendarDateSchema,
    bpdMm: z.number().positive().max(MAX_NUMERIC_5_1).optional(),
    hcMm: z.number().positive().max(MAX_NUMERIC_5_1).optional(),
    acMm: z.number().positive().max(MAX_NUMERIC_5_1).optional(),
    flMm: z.number().positive().max(MAX_NUMERIC_5_1).optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateUltrasoundBiometryRequest = z.infer<typeof createUltrasoundBiometryRequestSchema>;

export const ultrasoundBiometryResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  pregnancyEpisodeId: z.uuid(),
  encounterId: z.uuid().nullable(),
  scanDate: calendarDateSchema,
  gestationalAge: z.object({ weeks: z.number().int(), days: z.number().int() }),
  bpdMm: z.number().nullable(),
  hcMm: z.number().nullable(),
  acMm: z.number().nullable(),
  flMm: z.number().nullable(),
  estimatedFetalWeightGrams: z.number().nullable(),
  confidentialityReasons: z.array(z.string()),
});
export type UltrasoundBiometryResponse = z.infer<typeof ultrasoundBiometryResponseSchema>;

export const ultrasoundBiometryEnvelopeSchema = z.object({
  biometry: ultrasoundBiometryResponseSchema,
});
export const ultrasoundBiometryListEnvelopeSchema = z.object({
  biometryReadings: z.array(ultrasoundBiometryResponseSchema),
});

// --- Screening orders --------------------------------------------------------------------------------

export const createScreeningOrderRequestSchema = z
  .object({
    encounterId: z.uuid().optional(),
    screeningId: z.enum(OBGYN_SCREENING_IDS),
    orderedOn: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateScreeningOrderRequest = z.infer<typeof createScreeningOrderRequestSchema>;

export const recordScreeningResultRequestSchema = z
  .object({
    completedOn: calendarDateSchema,
    resultSummary: z.string().max(2000),
  })
  .strict();
export type RecordScreeningResultRequest = z.infer<typeof recordScreeningResultRequestSchema>;

export const screeningOrderResponseSchema = z.object({
  // Nullable: a screening the practice has never ordered still appears in the merged catalog list
  // (`screeningOrderService.ts`'s `listScreeningStatus`) with a computed due-window status and no
  // real row behind it yet.
  id: z.uuid().nullable(),
  patientId: z.uuid(),
  pregnancyEpisodeId: z.uuid(),
  encounterId: z.uuid().nullable(),
  screeningId: z.string(),
  orderedOn: calendarDateSchema.nullable(),
  completedOn: calendarDateSchema.nullable(),
  resultSummary: z.string().nullable(),
  dueStatus: z.string(),
  confidentialityReasons: z.array(z.string()),
});
export type ScreeningOrderResponse = z.infer<typeof screeningOrderResponseSchema>;

export const screeningOrderEnvelopeSchema = z.object({ order: screeningOrderResponseSchema });
export const screeningOrderListEnvelopeSchema = z.object({
  orders: z.array(screeningOrderResponseSchema),
});

// --- Postpartum (EPDS) screening ----------------------------------------------------------------------

export const createPostpartumScreeningRequestSchema = z
  .object({
    encounterId: z.uuid().optional(),
    screenedOn: calendarDateSchema,
    itemScores: z.array(z.number().int().min(0).max(EPDS_MAX_ITEM_SCORE)).length(EPDS_ITEM_COUNT),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreatePostpartumScreeningRequest = z.infer<
  typeof createPostpartumScreeningRequestSchema
>;

export const postpartumScreeningResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  pregnancyEpisodeId: z.uuid(),
  encounterId: z.uuid().nullable(),
  screenedOn: calendarDateSchema,
  itemScores: z.array(z.number().int()),
  totalScore: z.number().int(),
  selfHarmRisk: z.boolean(),
  riskLevel: z.string(),
  confidentialityReasons: z.array(z.string()),
});
export type PostpartumScreeningResponse = z.infer<typeof postpartumScreeningResponseSchema>;

export const postpartumScreeningEnvelopeSchema = z.object({
  screening: postpartumScreeningResponseSchema,
});
export const postpartumScreeningListEnvelopeSchema = z.object({
  screenings: z.array(postpartumScreeningResponseSchema),
});

// --- Antepartum billing suggestion (pure computed read, no persistence) ------------------------------

export const antepartumBillingSuggestionResponseSchema = z.object({
  code: z.string(),
  description: z.string(),
  note: z.string(),
});
export type AntepartumBillingSuggestionResponse = z.infer<
  typeof antepartumBillingSuggestionResponseSchema
>;

// --- Failure vocabulary --------------------------------------------------------------------------------

export const OBGYN_ERROR_CODES = [
  ...CLINICAL_ERROR_CODES,
  'not_entitled',
  'dating_already_locked',
  'delivery_already_recorded',
  'already_completed',
  'episode_not_delivered',
] as const;
export type ObgynErrorCode = (typeof OBGYN_ERROR_CODES)[number];
